import React, { useEffect, useMemo, useState } from 'react';
import {
  AnyAssetApplication,
  ColumnsConfig,
  CompactTable,
  defaultResponseHandler,
  Flex,
  insertArrayIf,
  openNotificationWithIcon,
  Page,
  PageContent,
  PageSidebar,
  Switch,
  useAssetConfig,
  useAssetParams,
  useAssetTranslation,
  useFilters,
  usePageQuery,
  useUserinfo,
} from '@mss/mss-component-library';
import Button from '../../Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faEllipsisV,
  faExclamationTriangle,
  faEye,
  faEyeSlash,
  faSync,
} from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Fetch from '../../util/_fetch';
import { Dropdown, Menu, Modal, notification, Tooltip } from 'antd';
import { sanitizedUrl } from '../../../asset/util/sanitize';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useDiscoveryQuery } from './queries';
import Breadcrumbs from '../../Breadcrumbs';
import {
  createCustomHeadingLabel,
  createCustomOptionLabels,
  getFilterKeysFromColumns,
  getSearchKeysFromColumns,
} from '../utils/filterHelpers';
import type { TFunction } from 'i18next';
import TimeAgo from 'react-timeago';

const permissionPrefix = ['add', 'delete', 'change'] as const;
type PermisionPrefix = (typeof permissionPrefix)[number];

type SharedColumnsProps = { t: TFunction; showHidden: boolean };

function sharedColumns({
  t,
  showHidden,
}: SharedColumnsProps): ColumnsConfig<any>[] {
  return [
    {
      title: t('Discovered'),
      dataIndex: 'discovered_at',
      key: 'discovered_at',
      width: 150,
      sorter: (a, b) => a.discovered_at.localeCompare(b.discovered_at) * -1,
      render: (time: any) => <TimeAgo date={new Date(time)} />,
    },
    {
      title: '',
      dataIndex: 'is_hidden',
      key: 'is_hidden',
      width: 66,
      sorter: showHidden ? a => (a.is_hidden ? -1 : 1) : undefined,
      render: (deleted: any) =>
        deleted ? (
          <small
            className="p-1"
            style={{
              borderRadius: 3,
              background: '#EDEDED',
              color: 'rgba(0,0,0,.50)',
            }}
          >
            hidden
          </small>
        ) : null,
    },
  ];
}

export default function List() {
  const { t } = useAssetTranslation();
  const [hasPermission] = useUserinfo();
  const { assetType } = useAssetParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const {
    config: { discovery, permissions: assetPermissions },
  } = useAssetConfig();
  const {
    manualDiscovery,
    canDeleteAsset,
    columns,
    permissions: _perms,
  } = discovery ?? {};

  const [discoveryEnabled, setDiscoveryEnabled] =
    useState<boolean>(!!manualDiscovery);
  const [showHidden, setShowHidden] = useState<boolean>(false);
  const [showDiscoveryConfigModal, setShowDiscoveryConfigModal] =
    useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const DiscoveryConfigModal = manualDiscovery?.DiscoveryConfigModal;
  const canAddAssets = hasPermission(assetPermissions.map(x => `add_${x}`));
  const queryClient = useQueryClient();

  function refetchAssets() {
    queryClient.invalidateQueries([assetType, 'discoveryAssets']);
  }

  const permissions = permissionPrefix.reduce(
    (acc, prefix) => ({
      ...acc,
      [prefix]: _perms?.map(item => `${prefix}_${item}`) ?? [],
    }),
    {} as Record<PermisionPrefix, string[]>
  );
  const canStartOnboarding = hasPermission(permissions.add);

  function hideAsset({ id, is_hidden }: { id: string; is_hidden: boolean }) {
    hideAssets.mutate({
      ids: [id],
      is_hidden: !is_hidden,
    });
  }

  function showDeleteModal(id: string) {
    Modal.confirm({
      title: t('Are you sure you want to delete?'),
      icon: (
        <span className="anticon">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            color="#fd9644"
            size="sm"
          />
        </span>
      ),
      content: t(
        `You're about to delete the asset. This asset won't be visible until you start a new asset discovery.`
      ),
      onOk: async () => {
        deleteAssets.mutate([id]);
        // await Api.deleteDiscoveredAsset(record.id);
        refetchAssets();
      },
    });
  }

  function renderActionsMenu(record: any) {
    return (
      <Dropdown
        disabled={selectedRowKeys.length > 0}
        overlay={
          <Menu>
            {hasPermission(permissions.change) && (
              <Menu.Item key="hide" onClick={() => hideAsset(record)}>
                {record.is_hidden ? 'Show' : 'Hide'}
              </Menu.Item>
            )}
            {hasPermission(permissions.delete) && (
              <Menu.Item
                key="delete"
                onClick={() => showDeleteModal(record.id)}
              >
                {t('Delete')}
              </Menu.Item>
            )}
          </Menu>
        }
        trigger={['click']}
      >
        <div style={{ width: 24 }}>
          <FontAwesomeIcon icon={faEllipsisV} />
        </div>
      </Dropdown>
    );
  }

  function renderHideButton(record: any) {
    return (
      <FontAwesomeIcon
        onClick={() => hideAsset(record)}
        icon={record.is_hidden ? faEye : faEyeSlash}
      />
    );
  }

  const _columns = useMemo(
    () => [
      ...(columns?.({ t }) ?? []),
      ...sharedColumns({ t, showHidden }),
      ...insertArrayIf(
        hasPermission([
          ...permissions.change,
          ...insertArrayIf(!!canDeleteAsset, permissions.delete),
        ]),
        [
          {
            key: 'actions',
            width: 40,
            render: (record: { id: string; is_hidden: boolean }) => (
              <Tooltip
                title={record.is_hidden ? t('Show asset') : t('Hide asset')}
                overlayStyle={{
                  display:
                    selectedRowKeys.length > 0 || canDeleteAsset
                      ? 'none'
                      : undefined,
                }}
              >
                <div
                  className={
                    canDeleteAsset
                      ? undefined
                      : `${selectedRowKeys.length > 0 ? 'opacity-10 pointer-events-none' : 'opacity-20 hover:opacity-100 hover:text-primary'}`
                  }
                  onClick={e => e.stopPropagation()}
                  style={{
                    opacity: selectedRowKeys.length > 0 ? 0.3 : 1,
                    width: 30,
                    textAlign: 'center',
                  }}
                >
                  {canDeleteAsset
                    ? renderActionsMenu(record)
                    : renderHideButton(record)}
                </div>
              </Tooltip>
            ),
          },
        ]
      ),
    ],
    [columns, selectedRowKeys.length, permissions, canDeleteAsset]
  );

  const discoveryQuery = useQuery({
    queryKey: [assetType, 'discoveryStatus'],
    queryFn: async () =>
      (
        await defaultResponseHandler<{
          running: boolean;
          started_at: string;
        }>({
          t,
          request: Fetch.get(`/api/assets/discovery/${assetType}/scan/`),
        })
      ).data ?? {
        running: false,
        started_at: '',
      },
    enabled: discoveryEnabled,
    refetchInterval: discoveryEnabled
      ? (manualDiscovery?.pollingInterval ?? 2500)
      : false,
  });

  const assetList = useDiscoveryQuery();

  const startDiscovery = useMutation({
    mutationFn: async (_body: Record<string, any>) => {
      const body = JSON.stringify(_body);
      const { error } = await defaultResponseHandler<any>({
        t,
        errorTitle: t('Failed to start discovery'),
        request: Fetch.post(`/api/assets/discovery/${assetType}/scan/`, {
          body,
        }),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setDiscoveryEnabled(true);
      openNotificationWithIcon(
        notification.success,
        t('Asset discovery started')
      );
    },
    onError: () => {
      openNotificationWithIcon(
        notification.error,
        t('Failed to start discovery')
      );
      setDiscoveryEnabled(false);
    },
  });

  const stopDiscovery = useMutation({
    mutationFn: async () => {
      const { error } = await defaultResponseHandler<any>({
        t,
        errorTitle: t('Failed to stop discovery'),
        request: Fetch.get(`/api/assets/discovery/${assetType}/revoke/`),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([assetType, 'discoveryStatus']);
    },
    onError: () => {
      setDiscoveryEnabled(false);
    },
  });

  const hideAssets = useMutation({
    mutationFn: async (args: { ids: string[]; is_hidden: boolean }) => {
      const { is_hidden, ids } = args;
      const results = await Promise.all(
        ids.map(id =>
          Fetch[discovery?.hideAssetMethod ?? 'put'](
            `/api/assets/discovery/${assetType}/${id}/`,
            {
              body: JSON.stringify({ is_hidden }),
            }
          )
        )
      );
      if (results.some(({ ok }) => !ok)) throw results;
    },
    onSuccess: (_, args) => {
      openNotificationWithIcon(
        notification.success,
        t(
          `Successfully ${args.is_hidden ? 'hidden' : 'unhidden'} ${args.ids.length > 1 ? 'assets' : 'asset'} from discovery.`
        )
      );
    },
    onError: (_, args) => {
      openNotificationWithIcon(
        notification.error,
        t(
          `Failed to hide ${args.ids.length > 1 ? 'assets' : 'asset'} from discovery.`
        )
      );
    },
    onSettled: () => {
      refetchAssets();
    },
  });

  const deleteAssets = useMutation({
    mutationFn: async (ids: string[]) => {
      const responses = await Promise.all(
        ids.map(id => Fetch.delete(`/api/assets/discovery/${assetType}/${id}/`))
      );
      const errorsIds = responses.reduce(
        (acc, { ok }, index) => (!ok ? [...acc, ids[index]] : acc),
        [] as string[]
      );
      if (errorsIds.length) throw errorsIds;
    },
    onSuccess: (_, ids) => {
      refetchAssets();
      openNotificationWithIcon(
        notification.success,
        t(
          `Successfully deleted ${ids.length > 1 ? 'assets' : 'asset'} from discovery.`
        )
      );
    },
    onError: (errorsIds: string[]) => {
      openNotificationWithIcon(
        notification.error,
        t('Failed to delete assets'),
        <ul>
          {errorsIds.map(id => (
            <li>{assetList.data?.find(x => x.id === id) ?? 'Unknown asset'}</li>
          ))}
        </ul>
      );
      refetchAssets();
    },
  });

  useEffect(() => {
    if (discoveryQuery.data?.running === false) {
      setDiscoveryEnabled(false);
      refetchAssets();
    }
  }, [discoveryQuery.data?.running]);

  function toggleDiscovery() {
    if (discoveryEnabled) {
      stopDiscovery.mutate();
    } else {
      setShowDiscoveryConfigModal(true);
    }
  }

  function closeModal() {
    setShowDiscoveryConfigModal(false);
  }

  const dataSource = (assetList.data ?? []).filter(
    x => showHidden || !x.is_hidden
  );

  const filterKeys = useMemo(
    () =>
      getFilterKeysFromColumns<
        AnyAssetApplication & { is_hidden: boolean; id: string }
      >(_columns),
    [_columns]
  );

  const searchKeys = useMemo(
    () =>
      getSearchKeysFromColumns<
        AnyAssetApplication & { is_hidden: boolean; id: string }
      >(_columns),
    [_columns]
  );

  const [availableFilters, toggleFilter, filteredData, search] = useFilters(
    dataSource,
    filterKeys,
    searchKeys,
    createCustomOptionLabels(_columns),
    createCustomHeadingLabel(_columns, t)
  );

  const [pageSize, setPageSize] = useState<number>(10);
  const [page, goToPage] = usePageQuery(filteredData.length / pageSize);

  const visibleAssetsSelected = selectedRowKeys.some(
    key => !assetList.data?.find(asset => asset.id === key)?.is_hidden
  );
  if (!discovery) return null;
  return (
    <Page
      breadCrumbs={<Breadcrumbs />}
      sidebar={
        !!filterKeys.length && (
          <PageSidebar
            availableFilters={availableFilters}
            toggleFilter={toggleFilter}
            search={search}
          />
        )
      }
      main={
        <PageContent
          title={t('Asset discovery')}
          actions={
            <Flex v="center">
              <Switch
                size="small"
                checked={showHidden}
                onChange={setShowHidden}
              />
              <label className="ml-2">{t('Show hidden assets')}</label>

              {manualDiscovery && hasPermission(permissions.add) && (
                <Button
                  loading={stopDiscovery.isLoading}
                  className="ml-4"
                  onClick={toggleDiscovery}
                >
                  <span>
                    <FontAwesomeIcon
                      icon={discoveryEnabled ? faBan : faSync}
                      className="mr-2"
                    />
                    {t(discoveryEnabled ? 'Stop discovery' : 'Start discovery')}
                  </span>
                </Button>
              )}
            </Flex>
          }
        >
          <CompactTable
            columns={_columns}
            dataSource={filteredData}
            rowKey="id"
            pagination={{
              current: page,
              showQuickJumper: true,
              showSizeChanger: true,
              total: filteredData.length,
              showTotal: (total: number, range: any[]) =>
                t('(RANGE1)-(RANGE2) of (TOTAL) items', {
                  range1: range[0],
                  range2: range[1],
                  TOTAL: total,
                }),
            }}
            onChange={(pagination: any) => {
              if (page !== pagination.current) goToPage(pagination.current);
              if (pagination.pageSize !== pageSize)
                setPageSize(pagination.pageSize);
            }}
            onRow={(record: any) => ({
              style: record.is_hidden ? { color: 'rgba(0,0,0,.4)' } : undefined,
              onClick: () =>
                setSelectedRowKeys(keys =>
                  keys.includes(record.id)
                    ? keys.filter(x => x !== record.id)
                    : [...keys, record.id]
                ),
            })}
            rowSelection={{
              selectedRowKeys,
              onChange: (newKeys: string[]) => setSelectedRowKeys(newKeys),
            }}
          />
          <Flex v="center" h="end" className={!dataSource.length ? 'mt-4' : ''}>
            {canDeleteAsset && hasPermission(permissions.delete) && (
              <Button
                disabled={selectedRowKeys.length === 0}
                loading={deleteAssets.isLoading || hideAssets.isLoading}
                className="mr-2"
                onClick={async () => {
                  await deleteAssets.mutateAsync(selectedRowKeys);
                  setSelectedRowKeys([]);
                }}
              >
                <FontAwesomeIcon icon={faBan} className="mr-2" />
                {t('Delete selected')}
              </Button>
            )}
            {hasPermission(permissions.change) && (
              <Button
                disabled={selectedRowKeys.length === 0}
                loading={deleteAssets.isLoading || hideAssets.isLoading}
                className="mr-2"
                onClick={async () => {
                  await hideAssets.mutateAsync({
                    ids: selectedRowKeys,
                    is_hidden: visibleAssetsSelected,
                  });
                  setSelectedRowKeys([]);
                }}
              >
                <FontAwesomeIcon
                  icon={visibleAssetsSelected ? faEyeSlash : faEye}
                  className="mr-2"
                />
                {visibleAssetsSelected
                  ? t('Hide selected')
                  : t('Show selected')}
              </Button>
            )}
            <Tooltip
              title={t(
                `You don't seem to have permission to add assets to this site. Please contact your administrator for help.`
              )}
              placement="bottom"
              overlayStyle={{ display: !canStartOnboarding ? undefined : 'none' }}
            >
              <span>
                <Button
                  data-ta="startOnboardingButton"
                  variant="primary"
                  onClick={() =>
                    history.push(
                      `${sanitizedUrl(url)}/add?ids=${selectedRowKeys.join(',')}`
                    )
                  }
                  disabled={!canStartOnboarding || selectedRowKeys.length === 0}
                >
                  {t('Start onboarding')}
                </Button>
              </span>
            </Tooltip>
          </Flex>
          {DiscoveryConfigModal && (
            <DiscoveryConfigModal
              open={showDiscoveryConfigModal}
              destroyOnClose
              mutate={startDiscovery.mutateAsync}
              onCancel={closeModal}
              onOk={closeModal}
            />
          )}
        </PageContent>
      }
    />
  );
}
